import { Injectable } from 'framework/di';

import {
  cartTemplate,
  filterOptionBoxTemplate,
  filterOptionListTemplate,
  filterOptionMultiLevelCollection,
  filterOptionMultiLevelTagTemplate,
  filterOptionRatingTemplate,
  filterOptionSwatchTemplate,
  preOrderTemplate,
  productPriceTemplate,
  refineByHorizontalTemplate,
  refineByTemplate,
  searchTabCollectionsTemplate,
  searchTabPagesTemplate,
  searchTabPaginationTemplate,
} from '../../template';
import { AppService } from '../app/app.service';
import { TemplateAPI } from '../template/template.api';
import { FILTER_TREE_STATE_KEY } from './filter.constants';
import { FilterFormatHelper } from './filter.format.helper';
import {
  Callback,
  EventName,
  EventPayload,
  type FilterAPIParams,
  type Guard,
  type Primitive,
} from './filter.model';
import { FilterStorageHelper } from './filter.storage.helper';
import { FilterStore } from './filter.store';
import { FilterUrlHelper } from './filter.url.helper';
import { FilterValidationHelper } from './filter.validation.helper';

/**
 * Central helper service providing utility functions for the filter module.
 *
 * This service acts as a unified facade for filter-related operations, delegating
 * to specialized helper services (URL, Format, Storage, Validation) while also providing
 * core utilities for JSON parsing, debouncing, event management, template handling,
 * and product URL construction. It can be extended by Technical Support teams to
 * customize filter behaviors, add shop-specific logic, or override default implementations.
 *
 * @remarks
 * Key responsibilities:
 * - Event management system (on, off, emit) for filter state changes
 * - Template resolution and customization
 * - Product URL construction with collection/variant handling
 * - Delegation to specialized helpers (URL, Format, Storage, Validation)
 * - Utility functions (debounce, JSON parsing, equality checks, sorting)
 * - Session/localStorage management
 * - Handler wrapper system for customization hooks
 *
 * The FilterHelper provides a convenient single point of access for most filter
 * operations while maintaining separation of concerns through specialized helpers.
 *
 * @example
 * Extend to customize URL building logic:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterHelper', (FilterHelper) => {
 *   return class CustomFilterHelper extends FilterHelper {
 *     buildProductDetailUrl(handle, hasCollection, currentTags) {
 *       // Add custom tracking parameter to all product URLs
 *       const baseUrl = super.buildProductDetailUrl(handle, hasCollection, currentTags);
 *       return baseUrl + '?utm_source=filter';
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class FilterHelper {
  constructor(
    private store: FilterStore,
    private appService: AppService,
    private urlHelper: FilterUrlHelper,
    private formatHelper: FilterFormatHelper,
    private storageHelper: FilterStorageHelper,
    private validationHelper: FilterValidationHelper,
    private templateAPI: TemplateAPI
  ) {}

  /**
   * Creates a debounced version of a function that delays execution until after a specified time.
   *
   * Useful for optimizing performance by delaying expensive operations (like API calls or DOM updates)
   * until after rapid consecutive calls have finished. The timer resets on each new invocation.
   * Override this method to customize debounce behavior or add logging for debugging.
   *
   * @param fn - The function to debounce
   * @param delay - Delay in milliseconds (default: 300ms)
   * @returns Debounced function that delays execution
   *
   * @example
   * Basic usage:
   * ```typescript
   * const debouncedSearch = this.filterHelper.debounce(() => {
   *   this.performSearch();
   * }, 500);
   * ```
   *
   * @example
   * Extend to add execution tracking:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHelper', (FilterHelper) => {
   *   return class CustomFilterHelper extends FilterHelper {
   *     debounce(fn, delay = 300) {
   *       const debouncedFn = super.debounce(fn, delay);
   *       return (...args) => {
   *         console.log('Debounced function called, will execute in', delay, 'ms');
   *         return debouncedFn(...args);
   *       };
   *     }
   *   };
   * });
   * ```
   */
  debounce<T extends (...args: any[]) => void>(fn: T, delay = 300) {
    let timer: ReturnType<typeof setTimeout> | undefined;

    return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
      if (timer) clearTimeout(timer);

      timer = setTimeout(() => {
        fn.apply(this, args);
      }, delay);
    };
  }

  /**
   * Safely parses JSON string with optional type guard validation.
   *
   * Provides safe JSON parsing that catches exceptions and returns null on failure.
   * Supports optional type guard function for runtime type validation. This is essential
   * for parsing user-provided data, metadata attributes, and API responses. Override this
   * method to add logging, custom error handling, or preprocessing of JSON strings.
   *
   * @param json - JSON string to parse
   * @param guard - Optional type guard function to validate parsed object matches expected type
   * @returns Parsed object if successful and passes guard (if provided), null otherwise
   *
   * @example
   * Basic usage without guard:
   * ```typescript
   * const data = this.filterHelper.safeParseJSON('{"key": "value"}');
   * // Returns: { key: "value" } or null if invalid
   * ```
   *
   * @example
   * With type guard:
   * ```typescript
   * interface Product { id: string; name: string; }
   * const isProduct = (obj: unknown): obj is Product =>
   *   typeof obj === 'object' && obj !== null && 'id' in obj && 'name' in obj;
   *
   * const product = this.filterHelper.safeParseJSON<Product>(
   *   productJson,
   *   isProduct
   * );
   * ```
   *
   * @example
   * Extend to add parsing analytics:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHelper', (FilterHelper) => {
   *   return class CustomFilterHelper extends FilterHelper {
   *     safeParseJSON(json, guard) {
   *       const result = super.safeParseJSON(json, guard);
   *       if (result === null) {
   *         // Track parsing failures for debugging
   *         analytics.track('json_parse_error', { json: json.substring(0, 100) });
   *       }
   *       return result;
   *     }
   *   };
   * });
   * ```
   */
  safeParseJSON(json: string): unknown | null;
  safeParseJSON<T>(json: string, guard: Guard<T>): T | null;

  // Implementation
  safeParseJSON<T>(json: string, guard?: Guard<T>): unknown | T | null {
    try {
      const parsed: unknown = JSON.parse(json);
      if (!guard) return parsed;
      return guard(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  isCollectionPage(): boolean {
    return this.appService.TAEAppConfig.generalSettings.page === 'collection';
  }
  /**
   * Builds a URLSearchParams object from filter API parameters
   * @param params - The parameters to convert to query string
   * @returns URLSearchParams object
   */
  buildQueryParams(params: FilterAPIParams): URLSearchParams {
    const queryParams = new URLSearchParams();
    queryParams.sort();
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        const value = params[key];
        if (value === undefined) continue;
        if (Array.isArray(value)) {
          value.forEach((item) => {
            queryParams.append(`${key}[]`, item);
          });
        } else {
          queryParams.set(key, `${value}`);
        }
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get('q');

    if (searchQuery) {
      queryParams.set('q', searchQuery);

      if (this.isCollectionPage()) {
        queryParams.set('incollection_search', 'true');
        queryParams.set('event_type', 'incollection_search');
      }
    }

    return queryParams;
  }

  /**
   * Adds locale parameter to query params if available
   * @param params - The parameters to enhance with locale
   * @returns Enhanced parameters with locale
   */
  addLocaleParam(params: FilterAPIParams): FilterAPIParams {
    const locale =
      (window as unknown as { Shopify?: { locale?: string } }).Shopify?.locale ||
      document.documentElement.lang ||
      'en';

    return {
      ...params,
      locale,
    };
  }

  /**
   * Checks if current page is a search page
   * @returns true if on search page
   */
  isSearchPage(): boolean {
    return (
      window.location.pathname.includes('/search') ||
      new URLSearchParams(window.location.search).has('q')
    );
  }

  /**
   * Checks if current page is a vendor page
   * @returns true if on vendor page
   */
  isVendorPage(): boolean {
    return window.location.pathname.includes('/vendors/');
  }

  /**
   * Checks if device is mobile based on breakpoint
   * @param breakpoint - The mobile breakpoint width
   * @returns true if mobile
   */
  isMobile(breakpoint: number = 575): boolean {
    return window.innerWidth <= breakpoint;
  }

  /**
   * Detects device type by width (mobile and/or tablet portrait)
   * @param mobileBreakpoint - Mobile breakpoint width (default 575)
   * @param tabletBreakpoint - Tablet portrait max breakpoint width (default 768)
   * @returns Device string (e.g., 'mobile|tablet_portrait_max' or empty)
   */
  detectDeviceByWidth(mobileBreakpoint: number = 575, tabletBreakpoint: number = 768): string {
    let result = '';
    if (window.innerWidth <= mobileBreakpoint) {
      result += 'mobile|';
    }
    if (window.innerWidth <= tabletBreakpoint) {
      result += 'tablet_portrait_max';
    }
    return result;
  }

  /**
   * Gets customer ID from window object
   * @returns Customer ID or null
   */
  getCustomerId() {
    // https://community.shopify.com/c/shopify-apis-and-sdks/current-best-way-to-get-logged-in-customer-id/td-p/482773
    return (
      window?.__st?.cid ||
      window?.meta?.page?.customerId ||
      window?.ShopifyAnalytics?.meta?.page?.customerId ||
      window?.ShopifyAnalytics?.lib?.user?.()?.traits?.()?.uniqToken
    );
  }

  /**
   * Generates a cache key from query parameters
   * @param queryParams - The query parameters
   * @returns Cache key string
   */
  generateCacheKey(queryParams: URLSearchParams): string {
    return queryParams.toString();
  }

  /**
   * Adds B2B parameters to query params if B2B is enabled
   * @param queryParams - URLSearchParams to enhance
   * @param b2bConfig - B2B configuration object
   */
  addB2BParams(
    queryParams: URLSearchParams,
    b2bConfig?: { enabled: boolean; [key: string]: unknown }
  ): void {
    if (!b2bConfig?.enabled) return;

    // Add B2B specific parameters
    if (b2bConfig.customerId) {
      queryParams.set('b2b_customer_id', String(b2bConfig.customerId));
    }
    if (b2bConfig.priceTier) {
      queryParams.set('b2b_price_tier', String(b2bConfig.priceTier));
    }
  }

  /**
   * Parses filter tree to flat parameters object - delegates to service
   * @param filterTree - The filter tree structure
   * @returns Flat parameters object with array values
   */
  parseFilterTreeToParams(filterTree: any[]): FilterAPIParams {
    const filterParams: FilterAPIParams = {};

    if (!Array.isArray(filterTree)) return filterParams;

    filterTree.forEach((filter) => {
      let { value } = filter.metaData || filter.data;
      const { key } = filter.metaData || filter.data;

      if (key.startsWith(`pf_st_`)) {
        value = this.convertValueRequestStockStatus(value);
      }

      const valueArray = Array.isArray(value) ? value : [value];
      const existingValues = filterParams[key] || [];
      filterParams[key] = Array.isArray(existingValues)
        ? [...existingValues, ...valueArray]
        : [...valueArray];
    });
    return filterParams;
  }

  /**
   * Converts stock status values for API requests.
   *
   * Transforms stock status filter values into boolean format for the API.
   * Converts 'out-of-stock' to false and all other values to true. Handles both
   * single values and arrays. Override this method to customize stock status
   * conversion logic for shop-specific requirements.
   *
   * @param value - Stock status value(s) from filter selection
   * @returns Boolean value(s) where false = out-of-stock, true = in-stock
   *
   * @example
   * Extend to support custom stock statuses:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHelper', (FilterHelper) => {
   *   return class CustomFilterHelper extends FilterHelper {
   *     protected convertValueRequestStockStatus(value) {
   *       if (Array.isArray(value)) {
   *         return value.map(v => {
   *           // Support custom 'low-stock' status
   *           if (v === 'out-of-stock' || v === 'low-stock') return false;
   *           return true;
   *         });
   *       }
   *       return value !== 'out-of-stock' && value !== 'low-stock';
   *     }
   *   };
   * });
   * ```
   */
  protected convertValueRequestStockStatus(value: any): boolean | boolean[] {
    if (Array.isArray(value)) {
      return value.map((v) => v !== 'out-of-stock');
    }
    return value !== 'out-of-stock';
  }

  /**
   * Extracts and processes HTML response
   * @param html - Raw HTML string
   * @returns Processed HTML string
   */
  processHTMLResponse(html: string): string {
    // Remove lazy loading attributes that might cause issues
    return html.replace(/loading="lazy"/g, '');
  }

  /**
   * Gets session ID for tracking
   * @returns Session ID
   */
  getSessionId(): string {
    const raw =
      (
        window as unknown as { boostWidgetIntegration?: { getSessionId?: () => string } }
      ).boostWidgetIntegration?.getSessionId?.() || '';

    if (raw.startsWith('"') && raw.endsWith('"') && raw.length > 2) {
      return raw.slice(1, -1);
    }
    return raw;
  }

  /**
   * Generates a unique ID
   * @returns Unique ID string
   */
  generateUuid(): string {
    return (
      (
        window as unknown as { boostWidgetIntegration?: { generateUuid?: () => string } }
      ).boostWidgetIntegration?.generateUuid?.() ||
      `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    );
  }

  getUrlScheme(): number {
    // urlScheme - 0: dont change url, 1: example 'color=red&color=blue', 2: example 'color=red,blue'
    return (
      (
        window as unknown as {
          boostWidgetIntegrationConfig?: { filter?: { urlScheme?: number } };
        }
      ).boostWidgetIntegrationConfig?.filter?.urlScheme || 2
    );
  }

  /**
   * Deletes a query parameter from the URL and updates browser history
   * Handles both shortened and full parameter names, sessionStorage cleanup, and URL schemes
   *
   * @param key - The query parameter key to delete (long form)
   * @param options - Options for history and URL handling
   */
  deleteQueryParamHistory(
    key: string,
    options: {
      preventPushHistory?: boolean;
      isShortenUrlParam?: boolean;
      urlScheme?: number;
    } = {}
  ): void {
    this.urlHelper.deleteQueryParamHistory(key, options);
  }

  deleteQueryParamsHistory(
    keys: Array<string>,
    options: {
      preventPushHistory?: boolean;
      isShortenUrlParam?: boolean;
      urlScheme?: number;
    } = {}
  ): void {
    this.urlHelper.deleteQueryParamsHistory(keys, options);
  }

  /**
   * Sets a query parameter in the URL and updates browser history
   * Supports multiple URL schemes and shortened parameter names
   *
   * @param key - The query parameter key to set (long form)
   * @param value - The value(s) to set (can be string or array)
   * @param replace - If true, use replaceState instead of pushState
   * @param options - Options for history and URL handling
   */
  setQueryParamHistory(
    key: string,
    value: string | string[],
    replace = false,
    options: {
      preventPushHistory?: boolean;
      isShortenUrlParam?: boolean;
      urlScheme?: number;
    } = {}
  ): void {
    this.urlHelper.setQueryParamHistory(key, value, replace, options);
  }

  /**
   * Appends a value to a query parameter in the URL
   * Used for multiple selection filters to add new values without replacing existing ones
   *
   * @param key - The query parameter key (long form)
   * @param value - The value to append
   * @param replace - Whether to replace history state (default: false)
   * @param options - Options for history and URL handling
   */
  appendQueryParamHistory(
    key: string,
    value: string,
    replace = false,
    options: {
      preventPushHistory?: boolean;
      isShortenUrlParam?: boolean;
      urlScheme?: number;
    } = {}
  ): void {
    this.urlHelper.appendQueryParamHistory(key, value, replace, options);
  }

  /**
   * Removes a specific value from a query parameter in the URL
   * This is different from deleteQueryParamHistory which removes the entire parameter
   *
   * @param key - The query parameter key to modify (long form)
   * @param value - The specific value to remove
   * @param options - Options for history and URL handling
   */
  removeQueryParamHistory(
    key: string,
    value: string,
    options: {
      isShortenUrlParam?: boolean;
      urlScheme?: number;
      preventPushHistory?: boolean;
    } = {}
  ): void {
    this.urlHelper.removeQueryParamHistory(key, value, options);
  }

  /**
   * Removes specific values from multiple query parameters in the URL
   * Handles both shortened and full parameter names with multiple URL schemes
   *
   * @param params - Array of {key, value} pairs to remove
   * @param options - Options for history and URL handling
   */
  removeQueryParamsHistory(
    params: Array<{ key: string; value: string }>,
    options: {
      isShortenUrlParam?: boolean;
      urlScheme?: number;
      preventPushHistory?: boolean;
    } = {}
  ): void {
    this.urlHelper.removeQueryParamsHistory(params, options);
  }

  /**
   * Sets the pagination page in session storage
   *
   * @param page - The page number to store
   */
  setPaginationSession(page: number | string): void {
    this.storageHelper.setPaginationSession(page);
  }

  /**
   * Gets a value from localStorage
   *
   * @param key - The localStorage key
   * @returns The stored value or null
   */
  getLocalStorage(key: string): string | null {
    return this.storageHelper.getLocalStorage(key);
  }

  getSessionStorage<T>(key: string, fallback: T): T {
    return this.storageHelper.getSessionStorage(key, fallback);
  }

  /**
   * Sets a value in localStorage
   *
   * @param key - The localStorage key
   * @param value - The value to store
   */
  setLocalStorage(key: string, value: string): void {
    this.storageHelper.setLocalStorage(key, value);
  }

  /**
   * Checks if multi-variant price should be displayed based on translation template
   *
   * @param amountTranslation - The translation template string
   * @returns true if translation contains minPrice/maxPrice placeholders
   */
  hasMultiVariantPriceFormat(amountTranslation?: string): boolean {
    return this.formatHelper.hasMultiVariantPriceFormat(amountTranslation);
  }

  /**
   * Checks if product has same min and max prices
   *
   * @param priceMin - Minimum price
   * @param priceMax - Maximum price
   * @param compareAtPriceMin - Minimum compare price
   * @param compareAtPriceMax - Maximum compare price
   * @returns true if min and max prices are identical
   */
  isSamePrice(
    priceMin: number | string,
    priceMax: number | string,
    compareAtPriceMin: number | string,
    compareAtPriceMax: number | string
  ): boolean {
    return this.formatHelper.isSamePrice(priceMin, priceMax, compareAtPriceMin, compareAtPriceMax);
  }

  /**
   * Checks if price transformation has already been applied to element
   *
   * @param element - DOM element to check
   * @param dataAttribute - Data attribute name to check
   * @returns true if already transformed
   */
  isPriceAlreadyTransformed(element: Element | null, dataAttribute = 'productPrice'): boolean {
    return this.formatHelper.isPriceAlreadyTransformed(element, dataAttribute);
  }

  /**
   * Validates if HTML contains actual price data (has digits)
   *
   * @param html - HTML string to validate
   * @returns true if HTML contains at least one digit
   */
  isValidPriceHTML(html?: string): boolean {
    return this.formatHelper.isValidPriceHTML(html);
  }

  /**
   * Formats a currency value according to shop settings
   *
   * @param options - Formatting options
   * @returns Formatted currency string or null
   */
  formatCurrency(options: {
    value: number | string | null | undefined;
    showCurrencyCodes?: boolean;
    showCentAsSuperscript?: boolean;
    removeDecimalPoint?: boolean;
    isPriceCompare?: boolean;
    removePriceDecimal?: boolean;
  }): string | null {
    return this.formatHelper.formatCurrency(options);
  }

  /**
   * Formats money value according to specified format options
   *
   * @param money - Money value to format
   * @param format - Format string
   * @param withTrailingZeros - Include trailing zeros
   * @param showCentAsSuperscript - Display cents as superscript
   * @param removePriceDecimal - Remove decimal part
   * @param removeDecimalPoint - Remove decimal point
   * @returns Formatted money string
   */
  formatMoney(
    money: number | string,
    format?: string,
    withTrailingZeros?: boolean,
    showCentAsSuperscript?: boolean,
    removePriceDecimal?: boolean,
    removeDecimalPoint?: boolean
  ): string {
    return this.formatHelper.formatMoney(
      money,
      format,
      withTrailingZeros,
      showCentAsSuperscript,
      removePriceDecimal,
      removeDecimalPoint
    );
  }

  /**
   * Strips HTML tags from string (delegated to validationHelper for consistency)
   *
   * @param html - HTML string
   * @returns Plain text string
   */
  stripHtml(html: string): string {
    return this.validationHelper.stripHtml(html);
  }

  getQueryParamByKey(key: string): string | null {
    return this.urlHelper.getQueryParamByKey(key);
  }

  /**
   * Parses URL search parameters and extracts filter params, sort, and pagination
   * Handles both shortened and full parameter formats
   *
   * @returns Object containing paramList, sort, and pagination
   */
  getParamsHistory(): {
    paramList: Array<{ key: string; values: string[] }>;
    sort: string | null;
    pagination: { page: string | null };
  } {
    return this.urlHelper.getParamsHistory();
  }

  checkExistFilterOptionParam = (isShortenUrlParam = false) => {
    return this.urlHelper.checkExistFilterOptionParam(isShortenUrlParam);
  };

  scrollToProduct(product) {
    const top =
      (window as any).boostWidgetIntegrationConfig?.scrollBackTopPosition ||
      product.getBoundingClientRect().top + window.scrollY;

    window.scroll({
      top,
      behavior: 'smooth',
    });
  }

  setSessionStorage<T>(key: string, value: T): void {
    this.storageHelper.setSessionStorage(key, value);
  }

  simpleMd5(input) {
    const inputLength = input.length;

    let a = 0x67452301;
    let b = 0xefcdab89;
    let c = 0x98badcfe;
    let d = 0x10325476;

    for (let i = 0; i < inputLength; i++) {
      a = a ^ input.charCodeAt(i);

      a = (a >>> 7) | (a << (32 - 7));

      c = (c + a + b) & 0xffffffff;
      b = (b >>> 17) | (b << (32 - 17));
      d = (d + c + a) & 0xffffffff;
      a = (a >>> 19) | (a << (32 - 19));

      b = b ^ input.charCodeAt(i);

      b = (b >>> 10) | (b << (32 - 10));

      c = (c + b + d) & 0xffffffff;
      d = (d >>> 22) | (d << (32 - 22));
      a = (a + c + b) & 0xffffffff;
      b = (b >>> 23) | (b << (32 - 23));
    }

    let md5Result =
      'w-' +
      [a, b, c, d]
        .map(function (i) {
          return i.toString(16).padStart(2, '0');
        })
        .join('');
    if (md5Result.startsWith('-')) {
      md5Result = md5Result.slice(1);
    }

    return md5Result;
  }

  //#region EVENTS
  on<T = unknown>(event: EventName, callback: Callback<T>): () => void {
    if (!this.store.events.value.has(event)) {
      this.store.events.value.set(event, []);
    }
    const callbacks = this.store.events.value.get(event)!;
    callbacks.push(callback as Callback);

    // Return unsubscribe
    return () => this.off(event, callback);
  }

  off<T = unknown>(event: EventName, callback: Callback<T>): void {
    if (this.store.events.value.has(event)) {
      const callbacks = this.store.events.value.get(event)!;
      const index = callbacks.indexOf(callback as Callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        if (callbacks.length === 0) {
          this.store.events.value.delete(event);
        }
      }
    }
  }

  emit<T = unknown>(event: EventName, data: T | undefined = undefined): void {
    if (this.store.events.value.has(event)) {
      // Clone để tránh mutate trong iterate
      const callbacks = [...this.store.events.value.get(event)!];
      callbacks.forEach((callback) => {
        (callback as Callback<T>)(data);
      });
    }
  }

  // Convenience: Emit dynamic event với pattern `${key}_changed`
  emitDynamic<T = unknown>(key: string, data: T & Partial<EventPayload> = {} as any): void {
    const eventName = `${key}_changed` as EventName;
    this.emit(eventName, { key, ...data } as T & EventPayload);
  }

  // Clear all listeners (cho cleanup)
  clear(): void {
    this.store.events.value.clear();
  }

  // Optional: Get all registered events (debug)
  getRegisteredEvents(): EventName[] {
    return Array.from(this.store.events.value.keys());
  }
  //#endregion

  //#region HANDLER WRAPPER
  /**
   * Wraps a callback function to handle customization logic.
   * Checks if custom handler functions are defined for the callback name
   * and block type, and wraps the original callback to call them before/after.
   * Allows overriding default handlers or injecting logic before/after calls.
   *
   * @param callback - The original callback function to wrap
   * @param handlerName - Optional custom name for the handler (defaults to callback.name)
   * @returns Wrapped async function that executes with customization hooks
   */
  createHandler<T extends (...args: any[]) => any>(
    callback: T,
    handlerName?: string
  ): (...args: Parameters<T>) => Promise<ReturnType<T>> {
    return async (...args: Parameters<T>): Promise<ReturnType<T>> => {
      const finalHandlerName = handlerName || callback.name;
      const blockType = this.store.getState('blockType');
      const customization = this.appService.TAEAppConfig.customization || {};

      // Determine the function to execute (allow override)
      let functionToCall = callback;

      // Global customization override
      if (
        customization[finalHandlerName] &&
        typeof customization[finalHandlerName] === 'function'
      ) {
        functionToCall = customization[finalHandlerName];
      }

      // Block-specific customization override
      if (
        customization[blockType]?.[finalHandlerName] &&
        typeof customization[blockType][finalHandlerName] === 'function'
      ) {
        functionToCall = customization[blockType][finalHandlerName];
      }

      // Build hook names
      const capitalizedHandlerName = finalHandlerName[0].toUpperCase() + finalHandlerName.slice(1);
      const beforeHandlerName = `before${capitalizedHandlerName}`;
      const afterHandlerName = `after${capitalizedHandlerName}`;

      // Execute global before hook
      if (
        customization[beforeHandlerName] &&
        typeof customization[beforeHandlerName] === 'function'
      ) {
        await customization[beforeHandlerName](...args);
      }

      // Execute block-specific before hook
      if (
        customization[blockType]?.[beforeHandlerName] &&
        typeof customization[blockType][beforeHandlerName] === 'function'
      ) {
        await customization[blockType][beforeHandlerName](...args);
      }

      // Execute main function
      const result = await functionToCall(...args);

      // Execute app integration hook
      if (
        customization[blockType]?.[finalHandlerName]?.appIntegration &&
        typeof customization[blockType][finalHandlerName].appIntegration === 'function'
      ) {
        customization[blockType][finalHandlerName].appIntegration(...args);
      }

      // Execute global after hook
      if (
        customization[afterHandlerName] &&
        typeof customization[afterHandlerName] === 'function'
      ) {
        await customization[afterHandlerName](result, ...args);
      }

      // Execute block-specific after hook
      if (
        customization[blockType]?.[afterHandlerName] &&
        typeof customization[blockType][afterHandlerName] === 'function'
      ) {
        await customization[blockType][afterHandlerName](result, ...args);
      }

      return result;
    };
  }
  //#endregion

  isEnterOrSpaceKeyPressed() {
    const key = this.store.getState(FILTER_TREE_STATE_KEY.KEY_CODE);
    // Check for both key names (string) and legacy key codes (string numbers)
    const isEnterOrSpace =
      key === 'Enter' || key === ' ' || key === 'Spacebar' || key === '32' || key === '13';
    return isEnterOrSpace;
  }

  /**
   * Mapping of template keys to metafield keys
   */
  private readonly TEMPLATE_MAPPING: Record<string, string> = {
    refineByTemplate: 'refineBy',
    refineByHorizontalTemplate: 'refineByHorizontal',
    filterOptionListTemplate: 'filterOptionListItem',
    filterOptionBoxTemplate: 'filterOptionBoxItem',
    filterOptionSwatchTemplate: 'filterOptionSwatchItem',
    filterOptionMultiLevelCollection: 'filterOptionMultiLevelCollectionItem',
    filterOptionMultiLevelTagTemplate: 'filterOptionMultiLevelTagItem',
    filterOptionRatingTemplate: 'filterOptionRatingItem',
    cartTemplate: 'cart',
    productPriceTemplate: 'productPrice',
    searchTabCollectionsTemplate: 'searchTabCollectionsContent',
    searchTabPagesTemplate: 'searchTabPagesContent',
    searchTabPaginationTemplate: 'searchTabCollectionsContentPagination',
    preOrderTemplate: 'preOrder',
  };

  /**
   * Default templates mapping
   */
  private readonly DEFAULT_TEMPLATES: Record<string, (...args: any[]) => string> = {
    refineByTemplate: () => refineByTemplate,
    refineByHorizontalTemplate: () => refineByHorizontalTemplate,
    filterOptionListTemplate: () => filterOptionListTemplate,
    filterOptionBoxTemplate: () => filterOptionBoxTemplate,
    filterOptionSwatchTemplate: () => filterOptionSwatchTemplate,
    filterOptionMultiLevelCollection: () => filterOptionMultiLevelCollection,
    filterOptionMultiLevelTagTemplate: () => filterOptionMultiLevelTagTemplate,
    filterOptionRatingTemplate: () => filterOptionRatingTemplate,
    cartTemplate: () => cartTemplate,
    productPriceTemplate: () => productPriceTemplate,
    searchTabCollectionsTemplate: () => searchTabCollectionsTemplate,
    searchTabPagesTemplate: () => searchTabPagesTemplate,
    searchTabPaginationTemplate: () => searchTabPaginationTemplate,
    preOrderTemplate: () => preOrderTemplate,
  };

  /**
   * Gets a specific template by key with customization support.
   *
   * Retrieves templates for rendering filter UI components, checking for customized
   * versions in `TAEAppConfig.templateParsed` or `TAEAppConfig.template` before falling
   * back to default templates. This is a critical customization point allowing shops to
   * completely override widget UI rendering. Override this method to add template
   * preprocessing, caching, or custom template resolution logic.
   *
   * @param templateKey - The template key to retrieve (e.g., `'filterOptionBoxTemplate'`)
   * @returns The customized or default template string
   * @throws {Error} If `templateKey` is not found in `DEFAULT_TEMPLATES`
   *
   * @remarks
   * Template resolution priority:
   * 1. `TAEAppConfig.templateParsed[metafieldKey]` (highest priority)
   * 2. `TAEAppConfig.template[metafieldKey]`
   * 3. `DEFAULT_TEMPLATES[templateKey]` (fallback)
   *
   * Available template keys:
   * - `refineByTemplate`, `refineByHorizontalTemplate`
   * - `filterOptionListTemplate`, `filterOptionBoxTemplate`, `filterOptionSwatchTemplate`
   * - `filterOptionMultiLevelCollection`, `filterOptionMultiLevelTagTemplate`
   * - `filterOptionRatingTemplate`, `cartTemplate`, `productPriceTemplate`
   * - `searchTabCollectionsTemplate`, `searchTabPagesTemplate`, `searchTabPaginationTemplate`
   * - `preOrderTemplate`
   *
   * @example
   * Basic usage:
   * ```typescript
   * const template = this.filterHelper.getTemplate('filterOptionBoxTemplate');
   * const html = await this.appService.templateRender(template, data);
   * ```
   *
   * @example
   * Extend to add template caching:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHelper', (FilterHelper) => {
   *   return class CustomFilterHelper extends FilterHelper {
   *     constructor(...args) {
   *       super(...args);
   *       this.templateCache = new Map();
   *     }
   *
   *     getTemplate(templateKey) {
   *       if (this.templateCache.has(templateKey)) {
   *         return this.templateCache.get(templateKey);
   *       }
   *       const template = super.getTemplate(templateKey);
   *       this.templateCache.set(templateKey, template);
   *       return template;
   *     }
   *   };
   * });
   * ```
   */
  getTemplate(templateKey: string): string {
    const defaultTemplate = this.DEFAULT_TEMPLATES[templateKey];
    if (!defaultTemplate) {
      throw new Error(`Template "${templateKey}" not found in default templates`);
    }

    const metafieldKey = this.TEMPLATE_MAPPING[templateKey];
    if (!metafieldKey) {
      return defaultTemplate();
    }

    const templateParsed = (this.appService.TAEAppConfig as any).templateParsed;
    if (templateParsed && templateParsed[metafieldKey]) {
      return templateParsed[metafieldKey];
    }

    const templates = this.appService.TAEAppConfig.template;
    if (templates && templates[metafieldKey]) {
      return templates[metafieldKey];
    }

    return defaultTemplate();
  }

  /**
   * Fetches templates from API with caching support.
   *
   * Retrieves templates by their IDs from the template API, with automatic
   * localStorage caching for performance. Checks cache first before making API calls.
   * This method is useful for dynamically loading templates that aren't included
   * in the initial app configuration.
   *
   * @param widgets - Single widget or array of widgets to fetch templates for
   * @param widgets[].name - Widget name (e.g., 'collectionHeader', 'recommendation')
   * @param widgets[].id - Widget template ID
   * @returns Promise resolving to object mapping template IDs to template strings
   *
   * @remarks
   * - Uses localStorage caching with 24-hour TTL
   * - Checks theme updated timestamp for cache invalidation
   * - Only fetches non-cached templates to minimize API calls
   * - Automatically caches fetched templates
   *
   * @example
   * Fetch single template:
   * ```typescript
   * const { templates } = await this.filterHelper.getTemplateByIds({
   *   name: 'collectionHeader',
   *   id: 'collection_header_id'
   * });
   * const template = templates['collection_header_id'];
   * ```
   *
   * @example
   * Fetch multiple templates:
   * ```typescript
   * const { templates } = await this.filterHelper.getTemplateByIds([
   *   { name: 'collectionHeader', id: 'header_id' },
   *   { name: 'recommendation', id: 'rec_id' }
   * ]);
   * ```
   *
   * @example
   * Extend to add custom caching logic:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHelper', (FilterHelper) => {
   *   return class CustomFilterHelper extends FilterHelper {
   *     async getTemplateByIds(widgets) {
   *       // Add custom pre-fetch logic
   *       console.log('Fetching templates:', widgets);
   *       const result = await super.getTemplateByIds(widgets);
   *       // Add custom post-fetch logic
   *       this.trackTemplateFetch(widgets);
   *       return result;
   *     }
   *   };
   * });
   * ```
   */
  async getTemplateByIds(
    widgets: { name: string; id: string | number } | Array<{ name: string; id: string | number }>
  ): Promise<{ templates: Record<string, string> }> {
    return this.templateAPI.getWidgetTemplate(widgets);
  }

  isEqual(value1: unknown, value2: unknown): boolean {
    // Quick check for different types
    if (typeof value1 !== typeof value2) {
      return false;
    }

    // Handle null/undefined
    if (value1 == null || value2 == null) {
      return value1 === value2;
    }

    // Special case for NaN
    if (
      typeof value1 === 'number' &&
      Number.isNaN(value1 as number) &&
      Number.isNaN(value2 as number)
    ) {
      return true;
    }

    // Primitives and functions/symbols use strict equality
    if (typeof value1 !== 'object') {
      return value1 === value2;
    }

    // Now both are non-null objects
    // Handle arrays explicitly
    const isArr1 = Array.isArray(value1);
    const isArr2 = Array.isArray(value2);
    if (isArr1 !== isArr2) {
      return false;
    }
    if (isArr1) {
      // Both arrays
      const arr1 = value1 as unknown[];
      const arr2 = value2 as unknown[];
      if (arr1.length !== arr2.length) {
        return false;
      }
      for (let i = 0; i < arr1.length; i++) {
        if (!this.isEqual(arr1[i], arr2[i])) {
          return false;
        }
      }
      return true;
    }

    // Both plain objects
    const obj1 = value1 as Record<string, unknown>;
    const obj2 = value2 as Record<string, unknown>;
    const keys1 = Object.keys(obj1).sort();
    const keys2 = Object.keys(obj2).sort();
    if (keys1.length !== keys2.length || keys1.join(',') !== keys2.join(',')) {
      return false;
    }

    // Recurse on values
    for (const key of keys1) {
      if (!this.isEqual(obj1[key], obj2[key])) {
        return false;
      }
    }

    return true;
  }

  /**
   * Sorts data by a specified key
   * Works with both arrays and objects
   *
   * @param data - Array or object to sort
   * @param key - Key to sort by
   * @param order - Sort order ('asc' or 'desc')
   * @returns Sorted array or object
   */
  sortByKey<T extends Record<string, any>>(
    data: T[] | Record<string, T>,
    key: string,
    order: 'asc' | 'desc' = 'asc'
  ): T[] | Record<string, T> | undefined {
    if (!Array.isArray(data) && typeof data !== 'object') {
      console.error('Invalid input. Expecting an array or an object.');
      return;
    }

    if (Array.isArray(data)) {
      return this.sortArrayByKey(data, key, order);
    } else {
      return this.sortObjectByKey(data as Record<string, T>, key, order);
    }
  }

  /**
   * Sorts an array by a specified key
   * Handles numeric (for numbers) and string values with lexical comparison (for strings, including numeric-looking strings)
   * Mixed number/string: attempts numeric if string is numeric, else lexical
   *
   * @param array - Array to sort
   * @param key - Key to sort by
   * @param order - Sort order ('asc' or 'desc')
   * @returns Sorted array
   */
  sortArrayByKey<T extends Record<string, any>>(
    array: T[],
    key: string,
    order: 'asc' | 'desc' = 'asc'
  ): T[] {
    const direction = order === 'desc' ? -1 : 1;

    return array.sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];

      // Handle null/undefined: treat as last in sort order
      if (aVal == null) {
        return bVal == null ? 0 : direction;
      }
      if (bVal == null) {
        return -direction;
      }

      let aEffective: number | string = aVal;
      let bEffective: number | string = bVal;
      let compareAsNum = false;

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        compareAsNum = true;
      } else if (
        (typeof aVal === 'number' && typeof bVal !== 'number') ||
        (typeof bVal === 'number' && typeof aVal !== 'number')
      ) {
        // Mixed: number and non-number (typically string)
        const strPart = typeof aVal === 'number' ? bVal : aVal;
        if (typeof strPart === 'string' && !isNaN(Number(strPart))) {
          compareAsNum = true;
          if (typeof aVal !== 'number') {
            aEffective = Number(strPart);
          } else {
            bEffective = Number(strPart);
          }
        }
        // If not numeric string, fall through to lexical
      }
      // Both non-numbers (e.g., strings): fall through to lexical

      if (compareAsNum) {
        // Numeric comparison
        const aN = aEffective as number;
        const bN = bEffective as number;
        if (Number.isNaN(aN)) {
          return Number.isNaN(bN) ? 0 : direction; // NaN last
        }
        if (Number.isNaN(bN)) {
          return -direction;
        }
        return (aN - bN) * direction;
      } else {
        // Lexical comparison (no numeric option for natural sort, to ensure lexical for numeric strings)
        const aS = String(aEffective);
        const bS = String(bEffective);
        return aS.localeCompare(bS, undefined, { sensitivity: 'base' }) * direction;
      }
    });
  }

  /**
   * Sorts an object by a specified key in its values
   *
   * @param object - Object to sort
   * @param key - Key to sort by
   * @param order - Sort order ('asc' or 'desc')
   * @returns Sorted object
   */
  sortObjectByKey<T>(
    object: Record<string, T>,
    key: string,
    order: 'asc' | 'desc' = 'asc'
  ): Record<string, T> {
    const entries = Object.entries(object);
    const sortedEntries = this.sortArrayByKey(
      entries.map(([k, v]) => ({ key: k, ...v }) as any),
      key,
      order
    );

    const result: Record<string, T> = {};
    for (const item of sortedEntries) {
      const { key: k, ...value } = item as any;
      result[k] = value as T;
    }

    return result;
  }

  /**
   * Rewrites the URL with new filter parameters, sort, and pagination
   * Clears existing filter params and sets new ones based on provided data
   *
   * @param data - Object containing paramList, sort, and pagination
   * @param options - Options for URL handling
   */
  rewriteUrlFromParams(
    data: {
      paramList: Array<{ key: string; values: string[] }>;
      sort: string | null;
      pagination: { page: string | null };
    },
    options: {
      isShortenUrlParam?: boolean;
      urlScheme?: number;
    } = {}
  ): void {
    this.urlHelper.rewriteUrlFromParams(data, options);
  }

  /**
   * Saves collection tags to session storage
   */
  saveCollectionTagsSlugKey(key: string, value: string): void {
    this.storageHelper.saveCollectionTagsSlugKey(key, value);
  }

  /**
   * Saves collection ID by handle to session storage
   */
  saveCollectionIdByHandle(handle: string, value: string): void {
    this.storageHelper.saveCollectionIdByHandle(handle, value);
  }

  /**
   * Gets collection tag from slug key
   */
  getCollectionTagFromSlugKey(key: string): string | undefined {
    return this.storageHelper.getCollectionTagFromSlugKey(key);
  }

  /**
   * Gets collection ID by handle key
   */
  getCollectionIdByHandleKey(key: string): string | number {
    return this.storageHelper.getCollectionIdByHandleKey(key);
  }
  isBadSearchTerm(term): boolean {
    return this.validationHelper.isBadSearchTerm(term);
  }
  sanitizeFilterLabel(label): string {
    return this.validationHelper.sanitizeFilterLabel(label);
  }

  escapeCssSelector(selector: string): string {
    // Escapes characters with special meaning in CSS selectors.
    // See: https://developer.mozilla.org/en-US/docs/Web/API/CSS/escape
    return CSS.escape(selector);
  }

  /**
   * Builds a product detail URL with optional collection context.
   *
   * Constructs the product detail page URL, optionally including collection context
   * in the path for better navigation and SEO. Handles locale prefixes, collection
   * handles, and tag-based URLs. This is a key extensibility point for customizing
   * product navigation behavior. Override to implement shop-specific URL structures,
   * add tracking parameters, or modify collection context logic.
   *
   * @param handle - Product handle (URL-safe product identifier)
   * @param hasCollection - Whether to include collection in URL path (default: false)
   * @param currentTags - Optional array of current page tags for tag-based URLs
   * @returns Complete product detail URL with optional collection context
   *
   * @remarks
   * URL patterns generated:
   * - Basic: `/products/{handle}`
   * - With locale: `/{locale}/products/{handle}`
   * - With collection: `/collections/{collection}/products/{handle}`
   * - With tags: `/collections/{collection}/{tag}`
   *
   * @example
   * Basic usage:
   * ```typescript
   * const url = this.filterHelper.buildProductDetailUrl('product-handle', true);
   * // Returns: "/collections/summer/products/product-handle"
   * ```
   *
   * @example
   * Extend to add analytics tracking:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHelper', (FilterHelper) => {
   *   return class CustomFilterHelper extends FilterHelper {
   *     buildProductDetailUrl(handle, hasCollection, currentTags) {
   *       const url = super.buildProductDetailUrl(handle, hasCollection, currentTags);
   *       // Add UTM parameters for tracking
   *       const separator = url.includes('?') ? '&' : '?';
   *       return url + separator + 'utm_source=filter&utm_medium=product_list';
   *     }
   *   };
   * });
   * ```
   */
  buildProductDetailUrl(
    handle: string,
    hasCollection: boolean = false,
    currentTags?: string[]
  ): string {
    if (!handle) return '/';

    const pathname = window.location.pathname;
    const elements = pathname.split('/');

    // Check if has locale
    let localeURLPart = '';
    localeURLPart = (
      (
        window as unknown as {
          Shopify?: { routes?: { root?: string } };
        }
      ).Shopify?.routes?.root || '/'
    ).replace(/\/$/, '');

    // Check if have hasCollection
    if (hasCollection) {
      // Homepage or Search page
      if (pathname === '/' || this.isSearchPage() || this.isVendorPage() || this.isPageType()) {
        return `${localeURLPart}/collections/all/products/${handle}`;
      } else if (this.isTagPage(currentTags)) {
        const preHandle = localeURLPart + '/collections/';
        const collectionHandleIndex = elements.indexOf('collections') + 1;
        if (elements.length >= 4)
          return preHandle + elements[collectionHandleIndex] + '/products/' + handle;

        return `${localeURLPart}/products/${handle}`;
      } else {
        const params = window.location.search.substring(1);
        // Google cache URL
        // An URL of Google cache will look like this: webcache.googleusercontent.com/search?q=cache:xxx:https://xxx.xxx/collections/xxx+&....
        if (params.indexOf('cache:') > -1) {
          let collectionHandle = 'all';
          const temp = params.split('&')[0].split('?')[0].split('collections/');
          if (temp.length > 1) {
            if (temp[1].indexOf('/') > -1) {
              collectionHandle = temp[1].split('/')[0];
            } else {
              collectionHandle = temp[1];
            }
          }
          collectionHandle = collectionHandle.replace(/[`~!@#$%^&*()_|+=?;:'",.<>{}[\]\\\/]/g, '');

          if (!collectionHandle) collectionHandle = 'all';

          return '/collections/' + collectionHandle + '/products/' + handle;
        }

        // Normal
        const collectionHandleIndex = elements.indexOf('collections') + 1;
        const preHandle = localeURLPart + '/collections/';
        if (typeof elements[2] !== 'undefined') {
          // Build for collection pages
          if (elements.includes('collections')) {
            const _collectionHandle = elements[collectionHandleIndex] || 'all';
            return preHandle + _collectionHandle + '/products/' + handle;
          }
        }
      }
    }

    return `${localeURLPart}/products/${handle}`;
  }

  /**
   * Builds a product detail URL with variant parameter.
   *
   * Constructs a complete product URL including the variant query parameter when needed.
   * Automatically appends ?variant={id} for split products or when a specific variant ID
   * is provided. This ensures the correct variant is selected when the customer lands on
   * the product page. Override to customize variant parameter handling or add additional
   * query parameters.
   *
   * @param product - Product object containing variants, handle, and split_product flag
   * @param hasCollection - Whether to include collection in URL path (default: false)
   * @param currentTags - Optional array of current page tags
   * @param variantId - Optional specific variant ID to use (overrides product.variant_id)
   * @returns Complete product detail URL with variant parameter if applicable
   *
   * @remarks
   * Variant parameter is added when:
   * - variantId argument is provided
   * - product.split_product is true AND variants exist
   * - Uses product.variant_id as fallback
   *
   * @example
   * Basic usage:
   * ```typescript
   * const url = this.filterHelper.buildProductDetailUrlWithVariant(
   *   { handle: 'tshirt', variants: [...], variant_id: 12345, split_product: true },
   *   true
   * );
   * // Returns: "/collections/apparel/products/tshirt?variant=12345"
   * ```
   *
   * @example
   * Extend to add custom parameters:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHelper', (FilterHelper) => {
   *   return class CustomFilterHelper extends FilterHelper {
   *     buildProductDetailUrlWithVariant(product, hasCollection, currentTags, variantId) {
   *       const url = super.buildProductDetailUrlWithVariant(product, hasCollection, currentTags, variantId);
   *       // Add color selection parameter
   *       if (product.selected_color) {
   *         const separator = url.includes('?') ? '&' : '?';
   *         return url + separator + 'color=' + product.selected_color;
   *       }
   *       return url;
   *     }
   *   };
   * });
   * ```
   */
  buildProductDetailUrlWithVariant(
    product: {
      variants?: unknown[];
      variant_id?: string | number;
      handle: string;
      split_product?: boolean;
    },
    hasCollection: boolean = false,
    currentTags?: string[],
    variantId?: string | number
  ): string {
    const { variants, variant_id, handle, split_product } = product;

    const variantIdPart =
      variantId || (split_product && variants) ? `?variant=${variantId || variant_id}` : '';

    const productUrl = this.buildProductDetailUrl(handle, hasCollection, currentTags);

    return productUrl + variantIdPart;
  }

  /**
   * Checks if current page is a tag page.
   *
   * Determines whether the current page is displaying tagged products by checking
   * for '/tagged/' in the URL path or the presence of currentTags array. This affects
   * how product URLs are constructed. Override this method to customize tag page
   * detection logic for shop-specific URL structures.
   *
   * @param currentTags - Optional array of current page tags
   * @returns True if on a tag page, false otherwise
   *
   * @example
   * Extend to support custom tag URL patterns:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHelper', (FilterHelper) => {
   *   return class CustomFilterHelper extends FilterHelper {
   *     protected isTagPage(currentTags) {
   *       const pathname = window.location.pathname;
   *       // Support custom '/products/tag/' pattern
   *       if (pathname.includes('/products/tag/')) return true;
   *       return super.isTagPage(currentTags);
   *     }
   *   };
   * });
   * ```
   */
  protected isTagPage(currentTags?: string[]): boolean {
    const pathname = window.location.pathname;
    return pathname.includes('/tagged/') || (Array.isArray(currentTags) && currentTags.length > 0);
  }

  /**
   * Checks if current page is a generic page type (not product/collection).
   *
   * Determines whether the current URL is a Shopify page (e.g., About, FAQ)
   * by checking for '/pages/' in the path. This affects URL construction logic.
   * Override this method to customize page type detection for shop-specific structures.
   *
   * @returns True if on a page type URL, false otherwise
   *
   * @example
   * Extend to support custom page patterns:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHelper', (FilterHelper) => {
   *   return class CustomFilterHelper extends FilterHelper {
   *     protected isPageType() {
   *       const pathname = window.location.pathname;
   *       // Also treat '/info/' paths as page types
   *       if (pathname.includes('/info/')) return true;
   *       return super.isPageType();
   *     }
   *   };
   * });
   * ```
   */
  protected isPageType(): boolean {
    return window.location.pathname.includes('/pages/');
  }

  /**
   * Slugifies a text string for URL usage
   * Delegates to FilterValidationHelper
   *
   * @param text - Text to slugify
   * @returns Slugified text
   */
  slugify(text: string): string {
    return this.validationHelper.slugify(text);
  }

  /**
   * Replaces trailing dot/comma and zeros from number string
   *
   * @param value - Number string to clean
   * @returns Cleaned number string
   */
  replaceDotCommaZero(value: string): string {
    return value.replace(/[.,]0+$/, '');
  }

  /**
   * Converts range value to string representation
   * Handles both object {lower, upper} and primitive values
   *
   * @param value - Value to convert (object with lower/upper or primitive)
   * @returns String representation of the value
   */
  toStringValue(value: any): string {
    if (typeof value === 'object' && value !== null) {
      return `${value.lower || 0}:${value.upper || 0}`;
    }
    return String(value);
  }

  /**
   * Converts string to range value object
   * Handles colon-separated range strings
   *
   * @param value - String value to convert (e.g., "10:50")
   * @returns Range object {lower, upper} or original value
   */
  toRangeValue(value: any): any {
    if (typeof value === 'string' && value.includes(':')) {
      const [lower, upper] = value.split(':').map(Number);
      return { lower, upper };
    }
    return value;
  }

  /**
   * Flattens nested tag structure into a flat object mapping tag to displayName
   *
   * @param array - Array of tag objects with potential subTags
   * @returns Object mapping tag keys to display names
   */
  flattenTagsToObject(array: any[]): Record<string, string> {
    const result: Record<string, string> = {};

    array.forEach((item) => {
      result[item.tag] = item.displayName;
      if (item.subTags) {
        Object.assign(result, this.flattenTagsToObject(item.subTags));
      }
    });

    return result;
  }

  /**
   * Checks if a node has nested children (tags or subTags)
   *
   * @param node - Node to check for nested children
   * @returns true if node has nested tags or subTags
   */
  hasNestedChildren(node: any): boolean {
    if (node.tags && Array.isArray(node.tags) && node.tags.length > 0) {
      return true;
    }
    if (node.subTags && Array.isArray(node.subTags) && node.subTags.length > 0) {
      return true;
    }
    return false;
  }
  /**
   * Converts string literals to their proper primitive types when possible.
   * - "true"/"false" => boolean
   * - "null" => null
   * - "undefined" => undefined
   * - numeric strings (strict decimal format) => number
   * - otherwise returns original value
   */
  toProperType = <T>(value: T): T | Primitive => {
    if (typeof value !== 'string') return value;

    const trimmed = value.trim();
    if (!trimmed) return value;

    switch (trimmed) {
      case 'true':
        return true;
      case 'false':
        return false;
      case 'null':
        return null;
      case 'undefined':
        return undefined;
    }

    // Strict numeric check (no hex, no scientific notation)
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      return Number(trimmed);
    }

    return value;
  };

  /**
   * Normalizes object values by attempting to coerce string values
   * into proper primitive types using `toProperType`.
   */
  normalizeObject = <T extends Record<string, unknown>>(
    obj: T
  ): { [K in keyof T]: Primitive | T[K] } => {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, this.toProperType(value)])
    ) as { [K in keyof T]: Primitive | T[K] };
  };
}
